import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GAME, PlayerState, UPGRADE_DEFINITIONS, UpgradePurchaseResult, UpgradeTree,
} from '@push.io/shared';
import { NetworkClient } from '../../network/client.js';
import { AudioManager } from '../../audio/manager.js';
import { getUpgradeHotkey } from './upgrade-config.js';
import './upgrade-bar.css';

const ORDER = [UpgradeTree.Push, UpgradeTree.Weight, UpgradeTree.Speed, UpgradeTree.Control] as const;
const ICONS: Record<UpgradeTree, React.ReactNode> = {
  [UpgradeTree.Push]: <svg viewBox="0 0 24 24"><path d="M3 12h6m6 0h6M7 8l4 4-4 4m10-8-4 4 4 4"/></svg>,
  [UpgradeTree.Weight]: <svg viewBox="0 0 24 24"><path d="M7 9h10l3 11H4L7 9Zm2 0a3 3 0 0 1 6 0"/></svg>,
  [UpgradeTree.Speed]: <svg viewBox="0 0 24 24"><path d="M3 7h8M2 12h7m-5 5h7l8-8-5 11"/></svg>,
  [UpgradeTree.Control]: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/><path d="M12 2v4m0 12v4M2 12h4m12 0h4"/></svg>,
};

function getLanguage(): 'es' | 'en' | 'fr' {
  try {
    const value = JSON.parse(localStorage.getItem('pushio_menu_settings') || '{}').language;
    return value === 'fr' ? 'fr' : value === 'es' ? 'es' : 'en';
  } catch { return 'es'; }
}

export function UpgradeBar({ player, network, audio, onTutorialPurchase, tutorialPurchaseEnabled = true, onBotMatchPurchase }: {
  player: PlayerState | null; network: NetworkClient; audio: AudioManager;
  onTutorialPurchase?: (stat: UpgradeTree) => boolean;
  tutorialPurchaseEnabled?: boolean;
  onBotMatchPurchase?: (stat: UpgradeTree) => boolean;
}) {
  const language = getLanguage();
  const tr = (es: string, en: string, fr: string) => language === 'es' ? es : language === 'fr' ? fr : en;
  const labels: Record<UpgradeTree, string> = {
    push: tr('Empuje', 'Push', 'Poussée'), weight: tr('Peso', 'Weight', 'Poids'),
    speed: tr('Velocidad', 'Speed', 'Vitesse'), control: tr('Control', 'Control', 'Contrôle'),
  };
  const descriptions: Record<UpgradeTree, string> = {
    push: tr('Aumenta la fuerza que transmites al chocar.', 'Increases impact force.', "Augmente la force d'impact."),
    weight: tr('Reduce cuánto te desplazan.', 'Reduces how far you are pushed.', 'Réduit la distance de poussée.'),
    speed: tr('Aumenta tu velocidad máxima.', 'Increases your maximum speed.', 'Augmente votre vitesse maximale.'),
    control: tr('Mejora giro, aceleración y recuperación.', 'Improves turning, acceleration and recovery.', "Améliore le virage, l'accélération et la récupération."),
  };
  const [pending, setPending] = useState<Partial<Record<UpgradeTree, string>>>({});
  const [feedback, setFeedback] = useState<{ stat: UpgradeTree; success: boolean; reason?: string; id: number } | null>(null);
  const [showHelp, setShowHelp] = useState(() => localStorage.getItem('pushio_upgrade_help_seen') !== '1');
  const [orbPulse, setOrbPulse] = useState(false);
  const previousOrbs = useRef(player?.orbs ?? 0);

  useEffect(() => {
    if (onTutorialPurchase) return;
    network.onUpgradeResultEvent((result: UpgradePurchaseResult) => {
      setPending((current) => {
        if (current[result.stat] !== result.requestId) return current;
        const next = { ...current };
        delete next[result.stat];
        return next;
      });
      const feedbackId = Date.now();
      setFeedback({ stat: result.stat, success: result.success, reason: result.reason, id: feedbackId });
      window.setTimeout(() => setFeedback((current) =>
        current?.id === feedbackId ? null : current), 900);
      if (result.success) {
        audio.playUpgradeSuccess(result.newLevel === GAME.MAX_UPGRADE_LEVEL);
        localStorage.setItem('pushio_upgrade_help_seen', '1');
        setShowHelp(false);
      } else {
        audio.playUpgradeRejected();
        if (navigator.vibrate) navigator.vibrate(25);
      }
    });
  }, [network, audio, onTutorialPurchase]);

  useEffect(() => {
    if ((player?.orbs ?? 0) > previousOrbs.current) {
      setOrbPulse(true);
      const timer = window.setTimeout(() => setOrbPulse(false), 420);
      previousOrbs.current = player?.orbs ?? 0;
      return () => clearTimeout(timer);
    }
    previousOrbs.current = player?.orbs ?? 0;
  }, [player?.orbs]);

  useEffect(() => {
    if (!showHelp) return;
    const timer = window.setTimeout(() => {
      setShowHelp(false);
      localStorage.setItem('pushio_upgrade_help_seen', '1');
    }, 7000);
    return () => clearTimeout(timer);
  }, [showHelp]);

  const purchase = (stat: UpgradeTree) => {
    if (!player?.alive || pending[stat] || player.upgrades[stat] >= GAME.MAX_UPGRADE_LEVEL) return;
    if (onBotMatchPurchase) {
      if (!onBotMatchPurchase(stat)) {
        audio.playUpgradeRejected();
        return;
      }
      const feedbackId = Date.now();
      setFeedback({ stat, success: true, id: feedbackId });
      window.setTimeout(() => setFeedback((current) => current?.id === feedbackId ? null : current), 900);
      audio.playUpgradeSuccess(false);
      setShowHelp(false);
      return;
    }
    if (onTutorialPurchase && !tutorialPurchaseEnabled) return;
    if (onTutorialPurchase) {
      if (!onTutorialPurchase(stat)) {
        audio.playUpgradeRejected();
        return;
      }
      const feedbackId = Date.now();
      setFeedback({ stat, success: true, id: feedbackId });
      window.setTimeout(() => setFeedback((current) => current?.id === feedbackId ? null : current), 900);
      audio.playUpgradeSuccess(false);
      setShowHelp(false);
      return;
    }
    const requestId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random()}`;
    setPending((current) => ({ ...current, [stat]: requestId }));
    network.sendUpgrade({
      requestId, stat, expectedCurrentLevel: player.upgrades[stat], clientTimestamp: Date.now(),
    });
  };

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      const stat = getUpgradeHotkey(event.key, event.repeat);
      const editableTarget = event.target instanceof Element
        && !!event.target.closest('input, textarea, select, [contenteditable="true"]');
      if (!stat || editableTarget) return;
      event.preventDefault();
      purchase(stat);
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  });

  const buttons = useMemo(() => ORDER.map((stat, index) => {
    const level = player?.upgrades[stat] ?? 0;
    const cost = level < GAME.MAX_UPGRADE_LEVEL ? UPGRADE_DEFINITIONS[stat][level].cost : null;
    return { stat, level, cost, hotkey: String(index + 1) };
  }), [player?.upgrades]);

  return <section className="upgrade-hud" data-game-input="ignore" aria-label={tr('Mejoras', 'Upgrades', 'Améliorations')}>
    {showHelp && <div className="upgrade-help"><strong>{tr('MEJORA DURANTE LA PARTIDA', 'UPGRADE DURING THE MATCH', 'AMÉLIOREZ PENDANT LA PARTIE')}</strong>
      <span>{tr('Pulsa 1–4 o toca un icono', 'Press 1–4 or tap an icon', 'Appuyez sur 1–4 ou touchez une icône')}</span></div>}
    <div className={`upgrade-orbs ${orbPulse ? 'pulse' : ''}`}><i /> <strong>{player?.orbs ?? 0}</strong></div>
    <div className="upgrade-bar">
      {buttons.map(({ stat, level, cost, hotkey }) => {
        const isPending = !!pending[stat];
        const maxed = cost === null;
        const available = cost !== null && (player?.orbs ?? 0) >= cost;
        const failed = feedback?.stat === stat && !feedback.success;
        const purchased = feedback?.stat === stat && feedback.success;
        const state = !player?.alive || (onTutorialPurchase && !tutorialPurchaseEnabled)
          ? 'disabled' : maxed ? 'maxed' : isPending ? 'pending' : available ? 'available' : 'locked';
        const availability = available ? tr('Disponible', 'Available', 'Disponible') : tr('No disponible', 'Unavailable', 'Indisponible');
        return <button key={stat} className={`upgrade-button ${state} ${failed ? 'failed' : ''} ${purchased ? 'purchased' : ''}`}
          disabled={!player?.alive || maxed || isPending || (!!onTutorialPurchase && !tutorialPurchaseEnabled)}
          aria-label={`${tr('Mejorar', 'Upgrade', 'Améliorer')} ${labels[stat]}. ${tr('Nivel', 'Level', 'Niveau')} ${level} ${tr('de', 'of', 'sur')} 4. ${maxed ? 'MAX' : `${tr('Coste', 'Cost', 'Coût')}: ${cost}. ${availability}`}`}
          title={descriptions[stat]}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.currentTarget.blur(); purchase(stat); }}>
          <span className="upgrade-icon">{ICONS[stat]}</span>
          <strong className="upgrade-name">{labels[stat]}</strong>
          <span className="upgrade-level" aria-hidden="true">
            {[0, 1, 2, 3].map((mark) => <i key={mark} className={mark < level ? 'on' : ''} />)}
          </span>
          <span className="upgrade-cost">{isPending ? '…' : maxed ? 'MAX' : <><i /> {cost}</>}</span>
          <kbd>{hotkey}</kbd>
        </button>;
      })}
    </div>
    {feedback && <div key={feedback.id} className={`upgrade-feedback ${feedback.success ? 'ok' : 'no'}`}>
      {feedback.success ? `${labels[feedback.stat].toUpperCase()} +1`
        : feedback.reason === 'insufficient_orbs' ? tr('ORBES INSUFICIENTES', 'NOT ENOUGH ORBS', 'ORBES INSUFFISANTS')
          : tr('MEJORA NO DISPONIBLE', 'UPGRADE UNAVAILABLE', 'AMÉLIORATION INDISPONIBLE')}
    </div>}
  </section>;
}
